import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '../../stores/userStore';
import {
  isPublicProfileSummaryPath,
  isPublicProfileRecordsPath,
  isPublicProfileBadgesPath,
  isMyProfileScrollPath,
  isSettingsScrollPath,
} from './routes';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { restoreSearchScroll } from '../search/useSearchScrollMemory';

const inputs = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
const profileQueries = new Set([
  'publicMusicRecords',
  'publicTaste',
  'myMusicRecords',
  'myTaste',
  'badges',
  'tasteGenreCatalog',
  'musicRecordsSearch',
]);

// A remounted profile can briefly be tall enough while its real content is still loading.
// Wait for data first, then reuse the bounded, user-interruptible height restoration.
export function restoreProfileScroll(target, ready, done = () => {}, waitAtTop = false) {
  let active = true,
    timer,
    stopHeight,
    stableHeight,
    stableSince;
  const deadline = Date.now() + 5000;
  const removeInputs = () =>
    inputs.forEach((event) => window.removeEventListener(event, cancel, true));
  function cancel() {
    if (!active) return;
    active = false;
    clearTimeout(timer);
    removeInputs();
    stopHeight?.();
    done(false);
  }
  function attempt() {
    if (!active) return;
    const loaded = ready();
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    if (!loaded || height !== stableHeight) {
      stableHeight = height;
      stableSince = Date.now();
    }
    // Measured titles/font scaling can commit once more after network requests settle.
    const settled = loaded && Date.now() - stableSince >= 150;
    if ((target === 0 && !waitAtTop) || settled || Date.now() >= deadline) {
      stopHeight = restoreSearchScroll(target, () => {
        if (!active) return;
        active = false;
        removeInputs();
        done(true);
      });
    } else timer = setTimeout(attempt, 50);
  }
  inputs.forEach((event) =>
    window.addEventListener(event, cancel, { capture: true, passive: true }),
  );
  // Let the committed route's queries start before inspecting fetch status.
  timer = setTimeout(attempt, 0);
  return cancel;
}

export function useProfileScrollMemory() {
  const location = useLocation(),
    navigationType = useNavigationType();
  const client = useQueryClient(),
    { locale } = useWebLocale();
  const viewer = useUserStore((s) => s.user?.id),
    admin = useUserStore((s) => s.isAdmin),
    login = useUserStore((s) => s.isLogin);
  const summary = isPublicProfileSummaryPath(location.pathname);
  const own = viewer != null && login && isMyProfileScrollPath(location.pathname);
  const ownSummary = own && /^\/mypage\/?$/.test(location.pathname);
  const badges = /\/badges\/?$/.test(location.pathname);
  const ownTaste = own && /\/taste\/?$/.test(location.pathname);
  const settings = isSettingsScrollPath(location.pathname);
  const managed =
    settings ||
    own ||
    (!admin &&
      (summary ||
        isPublicProfileRecordsPath(location.pathname) ||
        isPublicProfileBadgesPath(location.pathname)));
  const memory = useRef({ positions: new Map(), entry: null });
  const token = useRef(localStorage.getItem('POSTPOP_AUTH_TOKEN'));
  const scope = JSON.stringify([viewer ?? 'guest', Boolean(admin), Boolean(login), locale]);
  if (
    memory.current.scope !== scope ||
    token.current !== localStorage.getItem('POSTPOP_AUTH_TOKEN')
  ) {
    memory.current.entry?.cancel?.();
    token.current = localStorage.getItem('POSTPOP_AUTH_TOKEN');
    memory.current = { scope, positions: new Map(), entry: null };
  }
  // Catch even batched logout/login or token changes before the next route renders.
  useLayoutEffect(
    () =>
      useUserStore.subscribe((next, previous) => {
        if (
          next.user?.id !== previous.user?.id ||
          next.isAdmin !== previous.isAdmin ||
          next.isLogin !== previous.isLogin ||
          token.current !== localStorage.getItem('POSTPOP_AUTH_TOKEN')
        ) {
          memory.current.entry?.cancel?.();
          memory.current.positions.clear();
          memory.current.entry = null;
          token.current = localStorage.getItem('POSTPOP_AUTH_TOKEN');
        }
      }),
    [],
  );
  useLayoutEffect(() => {
    const store = memory.current;
    if (!managed) return undefined;
    const key = JSON.stringify([location.key, location.pathname, location.search]);
    const saved = navigationType === 'POP' ? store.positions.get(key) : undefined;
    const goalSheet =
      ownSummary && ['WEEK', 'MONTH'].includes(new URLSearchParams(location.search).get('goal'));
    const sheetTransition =
      ownSummary &&
      store.entry?.path === location.pathname &&
      (goalSheet || store.entry?.goalSheet);
    // Search, category and load-more controls replace the current URL without pushing a page.
    const target =
      saved?.y ??
      ((sheetTransition || navigationType === 'REPLACE') && store.entry?.path === location.pathname
        ? window.scrollY
        : 0);
    const entry = {
      ...saved,
      key,
      path: location.pathname,
      goalSheet,
      focus: saved?.focus ?? (sheetTransition ? store.entry?.focus : undefined),
      y: target,
      rails: { ...saved?.rails },
      restoring: true,
    };
    store.entry = entry;
    let active = true;
    const current = () =>
      active &&
      store === memory.current &&
      store.entry === entry &&
      token.current === localStorage.getItem('POSTPOP_AUTH_TOKEN');
    const root = () => {
      if (ownSummary) {
        const body = document.querySelector('.pp-my-summary-body');
        // The MY hero and body are siblings inside this route's PageLayout.
        return body?.parentElement ?? body;
      }
      return document.querySelector(
        settings
          ? '.pp-settings'
          : badges
            ? '.pp-badge-page'
            : ownSummary
              ? '.pp-my-summary-body'
              : ownTaste
                ? '.pp-taste.is-detail'
                : summary
                  ? '.pp-public-profile-summary'
                  : '.pp-my-records',
      );
    };
    const focusGroup = (link) => {
      const group = link.closest('[role="region"], section');
      return (
        group?.getAttribute('aria-label') ||
        Array.from(group?.classList || [])
          .filter((name) => !name.startsWith('is-'))
          .join(' ')
      );
    };
    const focusIdentity = (node) =>
      node.getAttribute('data-profile-focus-key')
        ? `control:${node.getAttribute('data-profile-focus-key')}`
        : node.getAttribute('href');
    const links = (href, group) =>
      Array.from(root()?.querySelectorAll('a, [data-profile-focus-key]') || []).filter(
        (a) => focusIdentity(a) === href && focusGroup(a) === group,
      );
    const railNodes = () =>
      Array.from(root()?.querySelectorAll('.pp-shelf-track, .pp-taste-decade-viewport') || []);
    const railKey = (node, index) => JSON.stringify([node.getAttribute('aria-label'), index]);
    const applyRails = () =>
      railNodes().forEach((node, index) => {
        const x = entry.rails[railKey(node, index)];
        // Taste starts centered on its strongest decade; only overwrite a saved position.
        if (x != null || node.matches('.pp-shelf-track')) node.scrollLeft = x ?? 0;
      });
    const save = (event) => {
      if (!current() || entry.restoring) return;
      entry.y = window.scrollY;
      const link = event?.target?.closest?.('a, [data-profile-focus-key]');
      if (link && root()?.contains(link)) {
        const href = focusIdentity(link),
          group = focusGroup(link);
        entry.focus = {
          href,
          group,
          label: link.getAttribute('aria-label') || link.textContent,
          index: links(href, group).indexOf(link),
        };
      }
      railNodes().forEach((node, index) => {
        entry.rails[railKey(node, index)] = node.scrollLeft;
      });
      store.positions.set(key, { y: entry.y, rails: { ...entry.rails }, focus: entry.focus });
      if (store.positions.size > 80) store.positions.delete(store.positions.keys().next().value);
    };
    if (badges && root()) root().dataset.profileRestoring = 'true';
    const oldPolicy = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    // Capture includes non-bubbling horizontal shelf scrolls.
    window.addEventListener('scroll', save, { capture: true, passive: true });
    window.addEventListener('click', save, true);
    window.addEventListener('focusin', save, true);
    const stop = restoreProfileScroll(
      target,
      () => {
        if (!current()) return false;
        applyRails();
        return (
          Boolean(root()) &&
          root()?.getAttribute('data-profile-records-ready') !== 'false' &&
          !client
            .getQueryCache()
            .findAll()
            .some(
              (q) =>
                (profileQueries.has(q.queryKey[0]) ||
                  (ownSummary &&
                    ((q.queryKey[0] === 'me' && q.queryKey[1] === 'archive') ||
                      q.queryKey[0] === 'sponsorships' ||
                      q.queryKey[0] === 'support-promotion'))) &&
                q.isActive() &&
                q.state.fetchStatus === 'fetching',
            )
        );
      },
      (completed) => {
        if (!current()) return;
        if (completed) {
          applyRails();
          const f = entry.focus;
          let candidate = f && links(f.href, f.group)[f.index];
          // Raising/deleting goals may remove the completion action. Its surviving
          // equivalent is this same archive's edit action, never another content card.
          if (!candidate && ownSummary && f?.href === 'control:archive-complete')
            candidate = root()?.querySelector('[data-profile-focus-key="archive-edit"]');
          // Never fall back to another occurrence of a missing content link.
          if (
            candidate &&
            (candidate.hasAttribute('data-profile-focus-key') ||
              (candidate.getAttribute('aria-label') || candidate.textContent) === f.label)
          )
            candidate.focus({ preventScroll: true });
        }
        if (badges && root()) delete root().dataset.profileRestoring;
        entry.restoring = false;
        save();
      },
      Boolean(saved),
    );
    entry.cancel = stop;
    return () => {
      if (badges && root()) delete root().dataset.profileRestoring;
      active = false;
      stop();
      entry.cancel = undefined;
      window.removeEventListener('scroll', save, true);
      window.removeEventListener('click', save, true);
      window.removeEventListener('focusin', save, true);
      window.history.scrollRestoration = oldPolicy;
    };
  }, [
    client,
    location,
    navigationType,
    managed,
    summary,
    ownSummary,
    ownTaste,
    badges,
    settings,
    scope,
  ]);
  return managed;
}
